import { useState, useEffect } from 'react'
import { usePulseMemory } from './usePulseMemory'
import { apiUrl, authFetch } from '../../lib/api'

export function PulseMemoryPanel() {
  const { memory, setProject, setGoal, setStack, addStack, removeStack, addRecentChange, removeRecentChange, clearRecentChanges, clear , addPattern, removePattern, addSelfNote, removeSelfNote } = usePulseMemory()
  const [newStack, setNewStack] = useState('')
  const [newChange, setNewChange] = useState('')
  const [projects, setProjects] = useState<{ id: string; name: string; slug?: string }[]>([])
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [autoDetected, setAutoDetected] = useState(false)

  // Auto-track file changes, agent patches, and AI fixes
  useEffect(() => {
    const onFileSaved = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.path) {
        const file = detail.path.split('/').pop()
        usePulseMemory.getState().addRecentChange(`Modified ${file}`)
        usePulseMemory.getState().addRecentFile(detail.path)
      }
    }
    const onAgentPatch = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.path) {
        const file = detail.path.split('/').pop()
        usePulseMemory.getState().addRecentChange(`AI patched ${file}`)
        usePulseMemory.getState().addRecentFile(detail.path)
        usePulseMemory.getState().setLastAction(`Fixed ${file}`)
      }
    }
    const onApproveFiles = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.content) {
        const fileMatches = detail.content.match(/\/\/ FILE:\s*(.+)/g) ?? []
        for (const m of fileMatches) {
          const path = m.replace(/\/\/ FILE:\s*/, '').trim()
          if (path === '__terminal__') continue
          const file = path.split('/').pop()
          usePulseMemory.getState().addRecentChange(`AI created/updated ${file}`)
          usePulseMemory.getState().addRecentFile(path)
        }
        if (fileMatches.length > 0) {
          usePulseMemory.getState().setLastAction(`AI updated ${fileMatches.length} file(s)`)
        }
      }
    }
    window.addEventListener('pulse:file-saved', onFileSaved)
    window.addEventListener('pulse:agent-file-patched', onAgentPatch)
    window.addEventListener('pulse:approve-files', onApproveFiles)
    return () => {
      window.removeEventListener('pulse:file-saved', onFileSaved)
      window.removeEventListener('pulse:agent-file-patched', onAgentPatch)
      window.removeEventListener('pulse:approve-files', onApproveFiles)
    }
  }, [])

  // Fetch user's projects from API + local workspace folders
  useEffect(() => {
    const fetchProjects = async () => {
      const results: { id: string; name: string; slug?: string }[] = []
      try {
        const res = await authFetch(apiUrl('/api/projects'))
        if (res.ok) {
          const data = await res.json()
          const list = Array.isArray(data) ? data : data.projects || []
          list.forEach((p: any) => results.push({ id: p.id, name: p.name, slug: p.slug }))
        }
      } catch {}
      try {
        const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
        const res = await fetch(apiUrl('/api/files/list'), { headers: { 'Authorization': `Bearer ${token}` } })
        if (res.ok) {
          const tree = await res.json()
          if (Array.isArray(tree)) {
            tree.filter((n: any) => n.type === 'folder').forEach((folder: any) => {
              if (!results.find(r => r.name === folder.name || r.slug === folder.name)) {
                results.push({ id: folder.path, name: folder.name })
              }
            })
          }
        }
      } catch {}
      setProjects(results)
      setLoadingProjects(false)
    }
    fetchProjects()
  }, [])

  // Auto-detect stack when project changes
  useEffect(() => {
    if (!memory.project) return
    const detect = async () => {
      try {
        const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
        const res = await fetch(apiUrl(`/api/files/read?path=${encodeURIComponent(memory.project + '/package.json')}`), {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (!res.ok) return
        const data = await res.json()
        const pkg = JSON.parse(data.content || '{}')
        const deps = { ...pkg.dependencies, ...pkg.devDependencies }
        const detected: string[] = []
        const checks: [string, string][] = [
          ['react', 'React'], ['next', 'Next.js'], ['vue', 'Vue'], ['svelte', 'Svelte'],
          ['express', 'Express'], ['fastify', 'Fastify'], ['@supabase/supabase-js', 'Supabase'],
          ['stripe', 'Stripe'], ['tailwindcss', 'Tailwind'], ['prisma', 'Prisma'],
          ['typescript', 'TypeScript'], ['drizzle-orm', 'Drizzle'], ['mongoose', 'MongoDB'],
          ['pg', 'PostgreSQL'], ['firebase', 'Firebase'], ['@trpc/server', 'tRPC'],
          ['zod', 'Zod'], ['vite', 'Vite'], ['webpack', 'Webpack'],
        ]
        for (const [dep, label] of checks) {
          if (deps[dep]) detected.push(label)
        }
        if (detected.length > 0) { setStack(detected); setAutoDetected(true) }
      } catch {}
    }
    detect()
  }, [memory.project, setStack])

  return (
    <div className="sidebar">
      <div className="sidebar__header">
        <span>PULSE MEMORY</span>
        <button className="sidebar__icon-btn" title="Clear all memory" onClick={clear}>↻</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Project selector */}
        <Section label="Project">
          <select
            value={memory.project}
            onChange={e => setProject(e.target.value)}
            style={selectStyle}
          >
            <option value="">-- Select project --</option>
            {projects.map(p => (
              <option key={p.id} value={p.name}>{p.name}</option>
            ))}
          </select>
          {loadingProjects && <span style={{ fontSize: 9, color: 'var(--text-2)' }}>Loading...</span>}
        </Section>

        {/* Current Goal */}
        <Section label="Current Goal">
          <input
            value={memory.goal}
            onChange={e => setGoal(e.target.value)}
            placeholder="What are you building right now?"
            style={inputStyle}
          />
          {!memory.goal && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 6 }}>
              {['Auth system', 'Stripe billing', 'Dashboard UI', 'REST API', 'Landing page', 'Database schema']
                .map(g => (
                  <button key={g} onClick={() => setGoal(g)} style={quickAddStyle}>{g}</button>
                ))}
            </div>
          )}
          {memory.goal && <GoalProgress />}
        </Section>

        {/* Known Stack */}
        <Section label="Known Stack">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
            {memory.stack.map(item => (
              <span key={item} style={tagStyle}>
                {item}
                <button onClick={() => removeStack(item)} style={tagRemoveStyle}>×</button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              value={newStack}
              onChange={e => setNewStack(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newStack.trim()) { addStack(newStack.trim()); setNewStack('') } }}
              placeholder="React, Node, Supabase..."
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={() => { if (newStack.trim()) { addStack(newStack.trim()); setNewStack('') } }} style={addBtn}>+</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 6 }}>
            {['React', 'Next.js', 'Vue', 'Node', 'TypeScript', 'Tailwind', 'Supabase', 'Prisma', 'Stripe', 'PostgreSQL']
              .filter(t => !memory.stack.includes(t))
              .slice(0, 5)
              .map(t => (
                <button key={t} onClick={() => addStack(t)} style={quickAddStyle}>{t}</button>
              ))}
          </div>
        </Section>

        {/* Recent Changes */}
        <Section label="Recent Changes">
          {memory.recentChanges.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
              {memory.recentChanges.slice(0, 5).map((change, i) => (
                <div key={i} style={{ fontSize: 10, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', padding: '3px 6px', background: 'var(--bg-2)', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{change}</span>
                  <button onClick={() => removeRecentChange(change)} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 10, padding: 0, lineHeight: 1 }}>×</button>
                </div>
              ))}
              <button onClick={clearRecentChanges} style={{ fontSize: 9, color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-mono)', textAlign: 'left', padding: '2px 0', marginTop: 2 }}>Clear all</button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              value={newChange}
              onChange={e => setNewChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newChange.trim()) { addRecentChange(newChange.trim()); setNewChange('') } }}
              placeholder="Added auth, fixed bug..."
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={() => { if (newChange.trim()) { addRecentChange(newChange.trim()); setNewChange('') } }} style={addBtn}>+</button>
          </div>
        </Section>

      {/* Coding Patterns (g3_patterns) */}
      <Section label="Coding Patterns">
        {(memory.patterns || []).length === 0 && <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>No patterns learned yet</div>}
        {(memory.patterns || []).map((p: string, i: number) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', padding: '2px 0' }}>
            <span style={{ flex: 1 }}>{p}</span>
            <button onClick={() => removePattern(p)} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9 }}>✕</button>
          </div>
        ))}
      </Section>

      {/* Self-Notes (g3_evolve) */}
      <Section label="Self-Notes">
        {(memory.selfNotes || []).length === 0 && <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>No self-corrections yet</div>}
        {(memory.selfNotes || []).map((n: string, i: number) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', padding: '2px 0' }}>
            <span style={{ flex: 1 }}>{n}</span>
            <button onClick={() => removeSelfNote(n)} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9 }}>✕</button>
          </div>
        ))}
      </Section>


                {/* Status indicator */}
        <div style={{ padding: '8px 10px', borderRadius: 6, background: 'rgba(95,212,164,0.06)', border: '1px solid rgba(95,212,164,0.15)' }}>
          <div style={{ fontSize: 9, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
            {memory.project && memory.goal && memory.stack.length > 0
              ? '✓ Pulse knows your project'
              : '◔ Fill in context for smarter AI'}
          </div>
          <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
            Injected into every AI conversation.
          </div>
          {autoDetected && (
            <div style={{ fontSize: 9, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>
              ⚡ Stack auto-detected from package.json
            </div>
          )}
          <div style={{ marginTop: 6, height: 3, borderRadius: 2, background: 'var(--bg-3)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: 'var(--accent-2)', transition: 'width 0.3s',
              width: `${Math.min(100, [memory.project, memory.goal, memory.stack.length > 0, memory.recentChanges.length > 0].filter(Boolean).length * 25)}%`,
            }} />
          </div>
        </div>
      </div>
    </div>
  )
}

function GoalProgress() {
  const { memory, addSubGoal, completeSubGoal, removeSubGoal, setGoalProgress } = usePulseMemory()
  const [newSub, setNewSub] = useState('')
  const entry = memory.goalsByProject[memory.project]
  const subGoals = entry?.subGoals ?? []
  const completedSubGoals = entry?.completedSubGoals ?? []
  const progress = memory.goalProgress

  return (
    <div style={{ marginTop: 8 }}>
      {/* Progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--bg-3)', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 2, background: progress >= 100 ? 'var(--accent-2)' : 'var(--accent)', transition: 'width 0.3s', width: `${progress}%` }} />
        </div>
        <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: progress >= 100 ? 'var(--accent-2)' : 'var(--text-2)', fontWeight: 700 }}>
          {progress}%
        </span>
      </div>
      {/* Sub-goals checklist */}
      {subGoals.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
          {subGoals.map(sub => {
            const done = completedSubGoals.includes(sub)
            return (
              <div key={sub} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontFamily: 'var(--font-mono)', color: done ? 'var(--accent-2)' : 'var(--text-1)' }}>
                <button
                  onClick={() => { if (!done) completeSubGoal(sub) }}
                  style={{ background: 'none', border: `1px solid ${done ? 'var(--accent-2)' : 'var(--border)'}`, borderRadius: 3, width: 14, height: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: 'var(--accent-2)', padding: 0 }}
                >{done ? '✓' : ''}</button>
                <span style={{ flex: 1, textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.6 : 1 }}>{sub}</span>
                <button onClick={() => removeSubGoal(sub)} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9, padding: 0 }}>×</button>
              </div>
            )
          })}
        </div>
      )}
      {/* Add sub-goal */}
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          value={newSub}
          onChange={e => setNewSub(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && newSub.trim()) { addSubGoal(newSub.trim()); setNewSub('') } }}
          placeholder="Add a step..."
          style={{ ...inputStyle, flex: 1, fontSize: 10 }}
        />
        <button onClick={() => { if (newSub.trim()) { addSubGoal(newSub.trim()); setNewSub('') } }} style={addBtn}>+</button>
      </div>
      {/* Manual progress slider when no sub-goals */}
      {subGoals.length === 0 && (
        <input
          type="range" min={0} max={100} value={progress}
          onChange={e => setGoalProgress(Number(e.target.value))}
          style={{ width: '100%', marginTop: 6, accentColor: 'var(--accent)' }}
        />
      )}


    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-2)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 5 }}>
        {label}
      </div>
      {children}
    
</div>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-2)',
  background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)',
  fontSize: 11, outline: 'none', width: '100%',
}
const selectStyle: React.CSSProperties = {
  ...inputStyle, cursor: 'pointer', appearance: 'none',
  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'8\' viewBox=\'0 0 8 8\'%3E%3Cpath fill=\'%23888\' d=\'M0 2l4 4 4-4z\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: 24,
}
const tagStyle: React.CSSProperties = {
  fontSize: 10, padding: '2px 8px', borderRadius: 4, background: 'rgba(95,212,164,0.12)',
  color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', fontWeight: 600,
  display: 'flex', alignItems: 'center', gap: 4,
}
const tagRemoveStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer',
  fontSize: 11, padding: 0, lineHeight: 1,
}
const addBtn: React.CSSProperties = {
  padding: '4px 8px', borderRadius: 6, border: '1px solid var(--accent)',
  background: 'rgba(124,110,245,0.06)', color: 'var(--accent)', cursor: 'pointer',
  fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
}
const quickAddStyle: React.CSSProperties = {
  fontSize: 9, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)',
  background: 'var(--bg-2)', color: 'var(--text-2)', cursor: 'pointer',
  fontFamily: 'var(--font-mono)', transition: 'all 0.1s',
}
